import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Discover, Wishlist, type VmpDiscoverProduct } from '@/lib/api/v1';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Input,
  Skeleton,
} from '@/design-system';
import { CatalogProductCard, catalogProductImageUrl } from '@/components/CatalogProductCard';
import { useToasts } from '@/lib/store';

/**
 * /utforsk — faceted catalog browse over the primary product catalog.
 *
 * Powered by `Discover.search()` which proxies vmpws/v2 via our backend.
 * State lives in the URL (search params) so any filtered view is shareable
 * and survives navigation. The category strip is hard-coded to align with
 * Alkisen' brand surfaces (vin/øl/sprit/sider); the country list is
 * built dynamically from the facets returned by the API on first load.
 */

type SortOption =
  | 'relevance'
  | 'name-asc'
  | 'name-desc'
  | 'price-asc'
  | 'price-desc'
  | 'alcohol-asc'
  | 'alcohol-desc';

interface CategoryOption {
  /** Primary catalog `mainCategory` facet code; empty = no filter. */
  code: string;
  label: string;
  icon: 'sparkles' | 'bottle' | 'beer' | 'spirits' | 'grape' | 'cherry';
}

const CATEGORIES: CategoryOption[] = [
  { code: '', label: 'Alle', icon: 'sparkles' },
  { code: 'rødvin', label: 'Rødvin', icon: 'bottle' },
  { code: 'hvitvin', label: 'Hvitvin', icon: 'bottle' },
  { code: 'rosévin', label: 'Rosé', icon: 'cherry' },
  { code: 'musserende_vin', label: 'Musserende', icon: 'bottle' },
  { code: 'sterkvin', label: 'Sterkvin', icon: 'bottle' },
  { code: 'brennevin', label: 'Brennevin', icon: 'spirits' },
  { code: 'øl', label: 'Øl', icon: 'beer' },
  { code: 'sider', label: 'Sider', icon: 'grape' },
];

const SORTS: { value: SortOption; label: string }[] = [
  { value: 'relevance', label: 'Relevans' },
  { value: 'name-asc', label: 'Navn A→Å' },
  { value: 'name-desc', label: 'Navn Å→A' },
  { value: 'price-asc', label: 'Pris stigende' },
  { value: 'price-desc', label: 'Pris synkende' },
  { value: 'alcohol-asc', label: 'Lavest alkohol' },
  { value: 'alcohol-desc', label: 'Høyest alkohol' },
];

const PAGE_SIZE = 24;

export function UtforskPage() {
  const [params, setParams] = useSearchParams();

  // Read URL state up-front. Defaults are blank so the URL stays clean
  // until the user actually filters something.
  const q = params.get('q') ?? '';
  const category = params.get('category') ?? '';
  const country = params.get('country') ?? '';
  const sort = (params.get('sort') as SortOption) || 'relevance';
  const page = Math.max(0, Number(params.get('page') ?? 0));

  // Local search input — debounced into the URL so the user can type without
  // each keystroke triggering a network call.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => setSearchInput(q), [q]);

  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => {
      updateParam('q', searchInput, { resetPage: true });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  function updateParam(name: string, value: string, opts: { resetPage?: boolean } = {}) {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(name, value);
    } else {
      next.delete(name);
    }
    if (opts.resetPage) next.delete('page');
    setParams(next, { replace: true });
  }

  const resultsQ = useQuery({
    queryKey: ['utforsk', q, category, country, sort, page],
    queryFn: () =>
      Discover.search({
        q: q || undefined,
        category: category || undefined,
        country: country || undefined,
        sort,
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });

  const products = resultsQ.data?.products ?? [];
  const total = resultsQ.data?.total ?? 0;
  const totalPages = resultsQ.data?.totalPages ?? 1;
  const facets = resultsQ.data?.facets ?? [];

  // Country facet — derived from the API response so the list reflects
  // what's actually available given the other filters.
  const countryFacet = useMemo(() => {
    return facets.find((f) => f.code === 'mainCountry') ?? null;
  }, [facets]);

  const topCountries = useMemo(() => {
    if (!countryFacet) return [];
    return [...countryFacet.values]
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [countryFacet]);

  const hasFilters = Boolean(q || category || country);

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Utforsk</h1>
        <p className="text-sm text-mist">
          Hele katalogen i én søkebar. Filtrer og finn neste flaske.
          {total > 0 && <span className="text-candle"> {total.toLocaleString('nb-NO')} produkter</span>}
        </p>
      </header>

      <Card className="grid gap-4 p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Input
            label="Søk"
            placeholder="Drue, område, produsent eller produktnavn"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="grid gap-1">
            <label htmlFor="utforsk-sort" className="text-xs uppercase tracking-widest text-mist">
              Sortér
            </label>
            <select
              id="utforsk-sort"
              value={sort}
              onChange={(e) => updateParam('sort', e.target.value, { resetPage: true })}
              className="ui-input"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              iconLeft="x"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              Nullstill
            </Button>
          )}
        </div>

        <div className="grid gap-2">
          <span className="text-xs uppercase tracking-widest text-mist">Kategori</span>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.code || 'all'}
                type="button"
                onClick={() => updateParam('category', c.code, { resetPage: true })}
                className={`ui-pill ${category === c.code ? 'ui-pill--active' : ''}`}
              >
                <Icon name={c.icon} size={12} />
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {topCountries.length > 0 && (
          <div className="grid gap-2">
            <span className="text-xs uppercase tracking-widest text-mist">Land</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateParam('country', '', { resetPage: true })}
                className={`ui-pill ${!country ? 'ui-pill--active' : ''}`}
              >
                Alle
              </button>
              {topCountries.map((v) => (
                <button
                  key={v.code}
                  type="button"
                  onClick={() => updateParam('country', v.code, { resetPage: true })}
                  className={`ui-pill ${country === v.code ? 'ui-pill--active' : ''}`}
                  title={`${v.count} produkter`}
                >
                  {v.name}
                  <span className="ml-1 text-[10px] text-mist">{v.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {resultsQ.isLoading && !resultsQ.data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-4">
              <Skeleton height={176} rounded="md" />
              <Skeleton height={18} width="80%" />
              <Skeleton height={12} width="60%" />
            </Card>
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon="search"
          title="Ingen treff"
          description={
            hasFilters
              ? 'Juster filtrene eller nullstill for å se hele utvalget.'
              : 'Vi fant ingen produkter akkurat nå.'
          }
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((p) => (
              <DiscoverCard key={p.code} product={p} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                iconLeft="chevron-left"
                disabled={page <= 0}
                onClick={() => updateParam('page', String(page - 1))}
              >
                Forrige
              </Button>
              <span className="text-sm text-mist">
                Side {page + 1} av {totalPages}
              </span>
              <Button
                variant="ghost"
                iconRight="chevron-right"
                disabled={page + 1 >= totalPages}
                onClick={() => updateParam('page', String(page + 1))}
              >
                Neste
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DiscoverCard({ product }: { product: VmpDiscoverProduct }) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const image = catalogProductImageUrl(product);
  const country = product.main_country?.name;
  const region = product.district?.name;

  const addWishlist = useMutation({
    mutationFn: () =>
      Wishlist.add({
        productId: product.code,
        name: product.name,
        region,
        country,
        imageUrl: image,
        priceNok: product.price?.value,
      }),
    onSuccess: () => {
      push({ title: 'Lagt på ønskelisten', description: product.name, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['wishlist'] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke legge til', description: err.message, tone: 'danger' }),
  });

  return (
    <CatalogProductCard
      product={product}
      action={
        <Button
          size="sm"
          variant="ghost"
          iconLeft="heart"
          loading={addWishlist.isPending}
          fullWidth
          onClick={() => addWishlist.mutate()}
        >
          Ønskeliste
        </Button>
      }
    />
  );
}
